/**
 * "You may also like" carousel for the cart drawer (Ego Cart Drawer clone).
 *
 * Sources, in order, until at least MIN_ITEMS products are found:
 *   a) predictive search on the cart's player / team metafield terms (data-terms)
 *   b) Shopify related recommendations for the cart's products (data-product-ids)
 *   c) a fallback collection (data-fallback-url)
 *
 * Every product on this shop is a one of one, so unavailable products are dropped.
 * All visible strings come from data attributes on the snippet, prices are formatted
 * with the shop currency. Survives section morphs by re-rendering from the cache.
 */

const MAX_ITEMS = 8;
const MIN_ITEMS = 4;
const MAX_TERMS = 6;
const MAX_RECOMMENDATION_SOURCES = 4;
const ERROR_VISIBLE_MS = 5000;

// StandardEvents.cartLinesUpdate from @shopify/events, used as a literal fallback
// when the bare module specifier cannot be resolved.
const CART_LINES_UPDATE = 'shopify:cart:lines-update';

const CART_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/><path d="M3 4h2.4l2.2 11.2a1.6 1.6 0 0 0 1.6 1.3h7.9a1.6 1.6 0 0 0 1.6-1.3L20.5 8H6.1"/></svg>';

/** @type {Promise<any> | null} */
let standardEventsModule = null;

/** Resolves the theme's @shopify/events module, or null when it is unavailable. */
function loadStandardEvents() {
  if (!standardEventsModule) {
    standardEventsModule = import('@shopify/events').catch(() => null);
  }
  return standardEventsModule;
}

function split(value) {
  return (value || '')
    .split('||')
    .map((s) => s.trim())
    .filter(Boolean);
}

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Removes markup and the few entities Shopify money strings can contain. */
function stripMarkup(value) {
  return String(value ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .trim();
}

/**
 * Formats an integer amount of cents with the shop's money_format string.
 * Used only when Intl.NumberFormat is not usable.
 */
function formatWithMoneyFormat(format, cents) {
  if (!format || !Number.isFinite(cents)) return '';
  const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
  if (!match) return stripMarkup(format);

  const name = match[1];
  const decimals = name.includes('no_decimals') ? 0 : 2;
  let thousands = ',';
  let decimal = '.';
  if (name.includes('comma_separator')) {
    thousands = '.';
    decimal = ',';
  } else if (name.includes('apostrophe_separator')) {
    thousands = "'";
    decimal = '.';
  } else if (name.includes('space_separator')) {
    thousands = ' ';
    decimal = ',';
  }

  const parts = (cents / 100).toFixed(decimals).split('.');
  const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  const amount = parts[1] ? whole + decimal + parts[1] : whole;
  return stripMarkup(format.replace(match[0], amount));
}

/** Converts a major unit string such as "45.00" or "45,00" to cents. */
function centsFromMajorString(value) {
  const cleaned = String(value ?? '').trim().replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const amount = Number.parseFloat(cleaned);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

async function getJson(url) {
  const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Request failed with ${response.status}`);
  return response.json();
}

/** Picks a usable image url from the different product shapes the APIs return. */
function imageOf(product) {
  const candidates = [
    product.featured_image,
    product.image,
    Array.isArray(product.images) ? product.images[0] : null,
    product.featured_media,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'string') return candidate;
    const url = candidate.url || candidate.src || candidate.preview_image?.src;
    if (url) return url;
  }
  return '';
}

function sizedImage(url) {
  if (!url) return '';
  const absolute = url.startsWith('//') ? `https:${url}` : url;
  return absolute + (absolute.includes('?') ? '&' : '?') + 'width=360';
}

class NfsCartUpsell extends HTMLElement {
  static observedAttributes = ['data-terms', 'data-exclude', 'data-product-ids', 'data-fallback-url'];

  #key = '';
  #products = [];
  #observer = null;
  #loadToken = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // Scroll events don't bubble but are observable in the capture phase.
    this.addEventListener('scroll', this.#onScroll, true);
    this.#observer = new MutationObserver(() => this.#restoreIfNeeded());
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'class'],
    });
    this.#maybeLoad();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#maybeLoad();
  }

  #text(name, fallback) {
    const value = this.dataset[name];
    return value && value.trim() ? value : fallback;
  }

  #maybeLoad() {
    const key = [
      this.dataset.terms || '',
      this.dataset.exclude || '',
      this.dataset.productIds || '',
      this.dataset.fallbackUrl || '',
    ].join('::');
    if (key === this.#key) return;
    this.#key = key;
    this.#load();
  }

  /* ------------------------------------------------------------------ pricing */

  /**
   * Formats cents with Intl using the shop locale and currency, falling back to
   * the shop's money_format string. Never returns "NaN".
   */
  #money(cents) {
    if (!Number.isFinite(cents)) return '';
    const currency = (this.dataset.currency || '').trim();
    const locale = (document.documentElement.lang || '').trim();
    if (currency) {
      try {
        return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency }).format(cents / 100);
      } catch {
        // Unsupported locale or currency code, use the money format instead.
      }
    }
    return formatWithMoneyFormat(this.dataset.moneyFormat, cents);
  }

  /**
   * Price for a /search/suggest.json result. The API returns `price` as an
   * integer number of cents in some versions and as a formatted string in others,
   * so never assume a unit: numbers are cents, strings are printed as they are.
   */
  #suggestPrice(product) {
    const raw = product.price;
    if (typeof raw === 'number' && Number.isInteger(raw)) return this.#money(raw);
    if (typeof raw === 'string') {
      const text = stripMarkup(raw);
      if (!text) return '';
      // A bare digit string is cents, anything else is already formatted money.
      if (/^\d+$/.test(text)) return this.#money(Number(text));
      return text;
    }
    return '';
  }

  /** Price for /products/*.js and /recommendations/products.json shapes (cents). */
  #productPrice(product) {
    if (typeof product.price === 'number' && Number.isFinite(product.price)) return this.#money(product.price);
    if (typeof product.price === 'string') {
      const text = stripMarkup(product.price);
      if (/^\d+$/.test(text)) return this.#money(Number(text));
      if (text) return text;
    }
    const variant = (product.variants || []).find((item) => item && item.available) || (product.variants || [])[0];
    if (variant && typeof variant.price === 'number') return this.#money(variant.price);
    const cents = variant ? centsFromMajorString(variant.price) : null;
    return cents === null ? '' : this.#money(cents);
  }

  /** Price for /collections/*\/products.json (variant prices are major units). */
  #collectionPrice(product) {
    const variant = (product.variants || []).find((item) => item && item.available) || (product.variants || [])[0];
    if (!variant) return '';
    const cents = centsFromMajorString(variant.price);
    return cents === null ? '' : this.#money(cents);
  }

  /* ---------------------------------------------------------------- normalize */

  #fromSuggest(product) {
    if (!product || !product.handle) return null;
    return {
      handle: product.handle,
      title: product.title || '',
      url: product.url || `/products/${product.handle}`,
      image: imageOf(product),
      price: this.#suggestPrice(product),
      available: product.available !== false,
    };
  }

  #fromProduct(product) {
    if (!product || !product.handle) return null;
    const variants = product.variants || [];
    const hasAvailableVariant = variants.length ? variants.some((variant) => variant && variant.available) : true;
    return {
      handle: product.handle,
      title: product.title || '',
      url: product.url || `/products/${product.handle}`,
      image: imageOf(product),
      price: this.#productPrice(product),
      available: product.available !== false && hasAvailableVariant,
    };
  }

  #fromCollection(product) {
    if (!product || !product.handle) return null;
    const variants = product.variants || [];
    return {
      handle: product.handle,
      title: product.title || '',
      url: `/products/${product.handle}`,
      image: imageOf(product),
      price: this.#collectionPrice(product),
      available: variants.some((variant) => variant && variant.available),
    };
  }

  /* -------------------------------------------------------------------- fetch */

  async #searchTerm(term) {
    try {
      const data = await getJson(
        `/search/suggest.json?q=${encodeURIComponent(
          term
        )}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide`
      );
      const products = data?.resources?.results?.products || [];
      return products.map((product) => this.#fromSuggest(product)).filter(Boolean);
    } catch {
      return [];
    }
  }

  async #recommendations(productId) {
    try {
      const data = await getJson(
        `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=related`
      );
      const products = data?.products || [];
      return products.map((product) => this.#fromProduct(product)).filter(Boolean);
    } catch {
      return [];
    }
  }

  async #fallbackProducts() {
    const base = (this.dataset.fallbackUrl || '').trim();
    if (!base) return [];
    const url = `${base.replace(/\/$/, '')}/products.json?limit=20`;
    try {
      // The .json endpoint is disabled on some shops and answers 404.
      const data = await getJson(url);
      const products = data?.products || [];
      return products.map((product) => this.#fromCollection(product)).filter(Boolean);
    } catch {
      return [];
    }
  }

  /* --------------------------------------------------------------------- load */

  async #load() {
    const token = ++this.#loadToken;
    const terms = split(this.dataset.terms).slice(0, MAX_TERMS);
    const exclude = new Set(split(this.dataset.exclude));
    const productIds = split(this.dataset.productIds).slice(0, MAX_RECOMMENDATION_SOURCES);
    const found = new Map();

    // Avoid showing an empty band while the first batch is in flight.
    if (!this.#products.length) this.hidden = true;

    const collect = (list) => {
      for (const item of list || []) {
        if (found.size >= MAX_ITEMS) return;
        if (!item || !item.handle) continue;
        if (!item.available) continue;
        if (exclude.has(item.handle) || found.has(item.handle)) continue;
        found.set(item.handle, item);
      }
    };

    // a) Metafield terms, all in parallel, first term's results first.
    if (terms.length) {
      const results = await Promise.all(terms.map((term) => this.#searchTerm(term)));
      if (token !== this.#loadToken) return;
      results.forEach(collect);
    }

    // b) Top up with Shopify related recommendations for the cart's products.
    if (found.size < MIN_ITEMS && productIds.length) {
      const results = await Promise.all(productIds.map((id) => this.#recommendations(id)));
      if (token !== this.#loadToken) return;
      results.forEach(collect);
    }

    // c) Top up from the fallback collection. This is the only source on an empty cart.
    if (found.size < MIN_ITEMS) {
      const results = await this.#fallbackProducts();
      if (token !== this.#loadToken) return;
      collect(results);
    }

    this.#products = [...found.values()].slice(0, MAX_ITEMS);
    this.#render();
  }

  /* ------------------------------------------------------------------- render */

  #render() {
    const list = this.querySelector('[data-nfs-list]');
    const dots = this.querySelector('[data-nfs-dots]');
    if (!list) return;

    if (!this.#products.length) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    const addLabel = this.#text('addLabel', 'Add to cart');

    list.innerHTML = this.#products
      .map(
        (product) => `
      <li class="nfs-upsell__item">
        <div class="nfs-upsell__card" data-nfs-card="${esc(product.handle)}">
          <a href="${esc(product.url)}" class="nfs-upsell__media" tabindex="-1">
            <img class="nfs-upsell__img" src="${esc(sizedImage(product.image))}" alt="" loading="lazy">
          </a>
          <div class="nfs-upsell__info">
            <a href="${esc(product.url)}" class="nfs-upsell__title">${esc(product.title)}</a>
            <div class="nfs-upsell__price">${esc(product.price)}</div>
            <p class="nfs-upsell__error" data-nfs-error hidden></p>
          </div>
          <button type="button" class="nfs-upsell__add" data-handle="${esc(product.handle)}" aria-label="${esc(
            addLabel
          )}">${CART_SVG}</button>
        </div>
      </li>`
      )
      .join('');

    if (dots) {
      const template = this.#text('dotLabel', 'Slide __INDEX__ of __LENGTH__');
      const total = this.#products.length;
      dots.innerHTML = this.#products
        .map((_, index) => {
          const label = template.replace('__INDEX__', String(index + 1)).replace('__LENGTH__', String(total));
          return `<button type="button" class="nfs-upsell__dot${
            index === 0 ? ' nfs-upsell__dot--active' : ''
          }" data-nfs-dot="${index}" aria-label="${esc(label)}"></button>`;
        })
        .join('');
    }

    const viewport = this.querySelector('[data-nfs-viewport]');
    if (viewport) viewport.scrollLeft = 0;
    this.#updateDots();
  }

  /**
   * The cart drawer content is morphed after every cart update, which wipes our
   * client-rendered cards. Rebuild from the cached product list when that happens.
   */
  #restoreIfNeeded() {
    const list = this.querySelector('[data-nfs-list]');
    if (!list || !this.#products.length) return;
    if (list.children.length === 0) {
      this.#render();
    } else if (this.hidden) {
      this.hidden = false;
    }
  }

  #onScroll = (event) => {
    if (event.target instanceof Element && event.target.hasAttribute('data-nfs-viewport')) {
      this.#updateDots();
    }
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addButton = target.closest('.nfs-upsell__add');
    if (addButton) {
      this.#add(addButton);
      return;
    }

    const viewport = this.querySelector('[data-nfs-viewport]');
    if (!viewport) return;

    const dot = target.closest('[data-nfs-dot]');
    if (dot) {
      viewport.scrollTo({ left: viewport.clientWidth * Number(dot.getAttribute('data-nfs-dot')), behavior: 'smooth' });
      return;
    }
    if (target.closest('[data-nfs-prev]')) {
      viewport.scrollBy({ left: -viewport.clientWidth, behavior: 'smooth' });
      return;
    }
    if (target.closest('[data-nfs-next]')) {
      viewport.scrollBy({ left: viewport.clientWidth, behavior: 'smooth' });
    }
  };

  /* ---------------------------------------------------------------------- add */

  /** Shows a short message on the card instead of failing silently. */
  #showCardMessage(button, message) {
    const card = button.closest('.nfs-upsell__card');
    const slot = card?.querySelector('[data-nfs-error]');
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = false;
    window.setTimeout(() => {
      if (slot.isConnected && slot.textContent === message) slot.hidden = true;
    }, ERROR_VISIBLE_MS);
  }

  #markSoldOut(button) {
    button.disabled = true;
    button.closest('.nfs-upsell__card')?.classList.add('is-soldout');
    this.#showCardMessage(button, this.#text('soldOutText', 'Sold out'));
  }

  /** Section ids the theme wants refreshed after a cart mutation. */
  #cartSectionIds() {
    const ids = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }
    return [...ids];
  }

  /**
   * Dispatches the theme's own cart refresh event. The theme (cart-drawer.js,
   * component-cart-items.js, product-form.js) listens for StandardEvents.cartLinesUpdate
   * ("shopify:cart:lines-update") and reads event.action plus a deferred event.promise.
   * Returns an object with resolve/reject so the caller can settle it.
   */
  async #dispatchCartLinesUpdate(variantId) {
    const events = await loadStandardEvents();
    const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;

    if (CartLinesUpdateEvent) {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
      return {
        resolve: (cartJson, sections) =>
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
            detail: {
              items: cartJson?.items,
              itemCount: cartJson?.item_count,
              sections,
              source: 'nfs-cart-upsell',
              sourceId: 'nfs-cart-upsell',
              didError: false,
            },
          }),
        reject: (error) => deferred.reject(error),
      };
    }

    // Fallback: same event name and shape, without the module.
    let settle = () => {};
    let fail = () => {};
    const promise = new Promise((resolve, reject) => {
      settle = resolve;
      fail = reject;
    });
    promise.catch(() => {});
    const event = new CustomEvent(CART_LINES_UPDATE, { bubbles: true });
    Object.assign(event, {
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise,
    });
    this.dispatchEvent(event);
    return {
      resolve: (cartJson, sections) =>
        settle({
          cart: null,
          detail: {
            items: cartJson?.items,
            itemCount: cartJson?.item_count,
            sections,
            source: 'nfs-cart-upsell',
            sourceId: 'nfs-cart-upsell',
            didError: false,
          },
        }),
      reject: (error) => fail(error),
    };
  }

  async #add(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('is-loading');

    let deferred = null;

    try {
      const handle = button.getAttribute('data-handle');
      const product = await getJson(`/products/${encodeURIComponent(handle)}.js`);
      const variant = (product.variants || []).find((item) => item && item.available);

      // One of one stock: never fall back to variants[0] when nothing is available.
      if (!variant) {
        this.#markSoldOut(button);
        button.classList.remove('is-loading');
        return;
      }

      const sectionIds = this.#cartSectionIds();
      const payload = { items: [{ id: variant.id, quantity: 1 }] };
      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }

      deferred = await this.#dispatchCartLinesUpdate(variant.id);

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });
      const addJson = await addResponse.json().catch(() => null);
      if (!addResponse.ok || addJson?.status) {
        throw new Error(addJson?.description || addJson?.message || 'Could not add to cart');
      }

      const cartJson = await getJson('/cart.js').catch(() => null);
      deferred.resolve(cartJson, addJson?.sections);
      deferred = null;

      // Legacy event kept for anything else on the page that listens to it.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: null, sourceId: 'nfs-cart-upsell', data: { source: 'nfs-cart-upsell' } },
        })
      );
    } catch (error) {
      deferred?.reject(error);
      console.error('[nfs-cart-upsell]', error);
      this.#showCardMessage(button, this.#text('errorText', 'Could not add, please try again'));
    } finally {
      if (!button.closest('.nfs-upsell__card')?.classList.contains('is-soldout')) {
        button.disabled = false;
      }
      button.classList.remove('is-loading');
    }
  }

  #updateDots() {
    const viewport = this.querySelector('[data-nfs-viewport]');
    const dots = this.querySelectorAll('[data-nfs-dot]');
    if (!viewport || !dots.length) return;
    const index = Math.round(viewport.scrollLeft / Math.max(viewport.clientWidth, 1));
    dots.forEach((dot, dotIndex) => dot.classList.toggle('nfs-upsell__dot--active', dotIndex === index));
  }
}

if (!customElements.get('nfs-cart-upsell')) {
  customElements.define('nfs-cart-upsell', NfsCartUpsell);
}
